import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { Formik, FormikValues, FormikErrors } from "formik";
import { bindActionCreators, Dispatch } from "redux";

import { Form, PickerInput, DataColumnDef, ITextQuery, IDataColumnDefProps, ApplyButton, OkButton, ValidationMessage, CancelLink } from "@plex/react-components";

import { validationMessages } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { Banner } from "ui/app";
import { useLocalizedValues, useLocalizer, useUserContext } from "ui/hooks";
import { ITerm } from "ui/screens";
import { displayMessages } from "ui/screens/translation-managers";
import styles from "ui/screens/translation-managers/message-details/MessageDetails.module.scss";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";

import { dictionaryLinksActions } from "../DictionaryLinks.actions";
import { IDictionaryLinksState, IAssociatedMessage } from "../DictionaryLinks.state";
import DictionaryLinksGrid from "../grid/DictionaryLinksGrid";

export interface IDictionaryLinksFormProps {
  loadPickerTerms: (criterion: IPickerSearchCriterion<ITerm>) => void;
  addAssociatedTerm: (payload: { message: IAssociatedMessage; associatedTerms: ITerm[]; redirect?: boolean }) => void;
  selectNewTerm: (term: ITerm[]) => void;
  cancelLinkClick: () => void;
  setValidationBanner: () => void;
  clearValidationNotification: () => void;
  messageId: string;
  termsPickerData: ITerm[];
  associatedTerms: ITerm[];
  newAssociatedTerm: ITerm[];
  notifications: INotification[];
}

const DictionaryLinksForm: React.FC<IDictionaryLinksFormProps> = ({
  loadPickerTerms,
  addAssociatedTerm,
  cancelLinkClick,
  setValidationBanner,
  clearValidationNotification,
  termsPickerData,
  messageId,
  associatedTerms,
  selectNewTerm,
  newAssociatedTerm,
  notifications
}) => {
  const localizedValues = useLocalizedValues(() => ({
    pickerDialogTitle: displayMessages.heading("dictionaryLinks"),
    termTitle: displayMessages.property("term"),
    descriptionTitle: displayMessages.property("description"),
    termsSectionTitle: displayMessages.heading("associatedTerms"),
    managedLinksLabel: displayMessages.property("manageLinks"),
    dictionaryLinksSectionTitle: displayMessages.property("dictionaryLinks"),
    messageAndTermAreRelatedError: validationMessages.error("termAndMessageAlreadyRelated")
  }));

  const localizer = useLocalizer();
  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();
  const isReadOnlyMode = !roles.PlexTechnicalWriter;
  const [redirectFlag, setRedirectFlag] = useState<boolean | undefined>(false);

  const onTermChange = useCallback(
    async (rows: ITerm[], validateForm: (values?: any) => Promise<FormikErrors<FormikValues>>, setFieldValidationValues: (values: FormikValues) => void) => {
      selectNewTerm(rows);
      setFieldValidationValues({
        newAssociatedTerm: rows
      });
      await validateForm();
    },
    [selectNewTerm]
  );

  const onSubmitButtonClick = useCallback((handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void, redirect?: boolean) => {
    handleSubmit();
    setRedirectFlag(redirect);
  }, []);

  const associatedTermExists = (termId: string, terms: ITerm[]): boolean => {
    return terms.some(t => t.termId === termId);
  };

  const onValidate = useCallback(
    (values: FormikValues) => {
      const errors: any = {};
      if (values.newAssociatedTerm.length === 0) {
        errors.manageLinksPicker = localizer(validationMessages.error("propertyIsRequired"), {
          property: localizedValues.managedLinksLabel
        });

        setValidationBanner();
      } else if (associatedTermExists(values.newAssociatedTerm[0].termId, associatedTerms)) {
        errors.manageLinksPicker = localizedValues.messageAndTermAreRelatedError;
        setValidationBanner();
      } else {
        clearValidationNotification();
      }

      return errors;
    },
    [associatedTerms, localizedValues.managedLinksLabel, localizedValues.messageAndTermAreRelatedError, localizer, clearValidationNotification, setValidationBanner]
  );

  const renderPicker = (
    errors: FormikErrors<FormikValues>,
    handleBlur: (e: React.FocusEvent<any>) => void,
    validateForm: (values?: any) => Promise<FormikErrors<FormikValues>>,
    setValues: (values: FormikValues) => void
  ) => {
    return (
      <PickerInput
        name="manageLinksPicker"
        className={errors.manageLinksPicker && styles.invalidField}
        dialogTitle={localizedValues.pickerDialogTitle}
        keySelector={(row: ITerm) => row.termId}
        displaySelector={(row: ITerm) => row.term}
        selected={newAssociatedTerm}
        data={termsPickerData}
        onBlur={handleBlur}
        disabled={isReadOnlyMode}
        onSelectionChanged={rows => onTermChange(rows as ITerm[], validateForm, setValues)}
        onSearch={(query: ITextQuery, columns: Array<IDataColumnDefProps<ITerm>>) => loadPickerTerms({ query: query, searchedColumns: columns })}
      >
        <DataColumnDef<ITerm> key="1" id="term" searchable={true} sortable={true} title={localizedValues.termTitle} valueSelector={row => row.term}>
          {(row: ITerm) => row.term}
        </DataColumnDef>
        <DataColumnDef<ITerm> key="2" id="definition" searchable={true} sortable={true} title={localizedValues.descriptionTitle} valueSelector={row => row.definition}>
          {(row: ITerm) => row.definition}
        </DataColumnDef>
      </PickerInput>
    );
  };

  const renderAssociatedTermsSection = () => {
    return (
      <Form.Section title={localizedValues.termsSectionTitle}>
        <Form.Row>
          <DictionaryLinksGrid />
        </Form.Row>
      </Form.Section>
    );
  };

  return (
    <Formik
      initialValues={{
        newAssociatedTerm: []
      }}
      validate={onValidate}
      onSubmit={(_: FormikValues, { setSubmitting }) => {
        setSubmitting(false);

        addAssociatedTerm({
          message: {
            messageId: messageId,
            term: newAssociatedTerm[0]
          },
          associatedTerms: associatedTerms,
          redirect: redirectFlag
        });
      }}
    >
      {({ handleSubmit, handleBlur, errors, setValues, validateForm }) => (
        <Form className={styles.messageFormTab}>
          <Form.Header>
            <Banner messages={notifications} />
          </Form.Header>
          <Form.Body className={styles.formBody}>
            <Form.Section title={localizedValues.dictionaryLinksSectionTitle}>
              <Form.Row id="manage-links-picker">
                <Form.FieldPair required labelText={localizedValues.managedLinksLabel}>
                  {renderPicker(errors, handleBlur, validateForm, setValues)}
                  <ValidationMessage message={errors.manageLinksPicker ? (errors.manageLinksPicker as string) : ""} />
                </Form.FieldPair>
              </Form.Row>
            </Form.Section>
            {associatedTerms.length > 0 && renderAssociatedTermsSection()}
          </Form.Body>
          <Form.Footer className={styles.formFooter}>
            <CancelLink onClick={cancelLinkClick} />
            {roles.PlexTechnicalWriter && (
              <>
                <ApplyButton onClick={() => onSubmitButtonClick(handleSubmit)} />
                <OkButton onClick={() => onSubmitButtonClick(handleSubmit, true)} />
              </>
            )}
          </Form.Footer>
        </Form>
      )}
    </Formik>
  );
};

export default connect(
  (state: { dictionaryLinks: IDictionaryLinksState }) => {
    return {
      newAssociatedTerm: state.dictionaryLinks.newAssociatedTerm,
      termsPickerData: state.dictionaryLinks.termsPickerData,
      notifications: state.dictionaryLinks.notifications
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        selectNewTerm: dictionaryLinksActions.selectNewTerm,
        loadPickerTerms: dictionaryLinksActions.loadPickerTerms,
        addAssociatedTerm: dictionaryLinksActions.addAssociatedTerm,
        cancelLinkClick: dictionaryLinksActions.cancelLinkClick,
        setValidationBanner: dictionaryLinksActions.setValidationBanner,
        clearValidationNotification: dictionaryLinksActions.clearValidationNotification
      },
      dispatch
    )
)(DictionaryLinksForm);
